import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { apiRequest } from '../../utils';
import { CustomModal } from '../CustomModal';
import type { SummaryKybRelatedDocuments } from '../../types/summaryKybRelatedDocuments';

interface CompanyDocumentModalProps {
  trigger: React.ReactElement;
  organizationId: string;
}

/**
 * CompanyDocumentModal Component
 * 
 * A modal that displays the full CompanyDocuments component.
 * Use this from other components to show the document list in a modal.
 * 
 * @example
 * // In another component (e.g., KybStepsConfirmation)
 * <CompanyDocumentModal
 *   trigger={<button className="btn btn-primary">Ver Documentos</button>}
 *   organizationId="12345"
 * />
 */
export const CompanyDocumentModal: React.FC<CompanyDocumentModalProps> = ({
  trigger,
  organizationId,
}) => {
  const { t } = useTranslation();

  return (
    <CustomModal
      trigger={trigger}
      title={t('kyb_flow.company_documents.modal_title')}
      description={t('kyb_flow.company_documents.description')}
      size="md"
    >
      <CompanyDocuments organizationId={organizationId} />
    </CustomModal>
  );
};

const DocumentItem: React.FC<DocumentItemProps> = ({ title, description, hasMore, onClick, lastItem, children }) => {
  const { t } = useTranslation();

  return (
    <div className={`d-flex ${lastItem ? '' : 'border-bottom border-gray-300'} justify-content-between gap-3 pb-3`}>
      <div className="d-flex flex-column text-start">
        <span className="font-weight-600">{title}</span>
        <span className="size-14 font-weight-400 fst-italic text-muted">{description}</span>
        {hasMore && (
          <>
            {children || (
              <button
                className="btn btn-link text-decoration-none hyperlink d-flex size-14"
                onClick={onClick}
              >
                {t('see_details')}
              </button>
            )}
          </>
        )}
      </div>
      <div className="d-flex align-items-center">
        <span className="badge pill-success size-12 font-weight-400">{t('included')}</span>
      </div>
    </div>
  );
};

export const CompanyDocuments: React.FC<CompanyDocumentsProps> = ({
  organizationId,
}) => {
  const { t } = useTranslation();
  
  const [submitting, setSubmitting] = useState(false);
  const [documents, setDocuments] = useState<SummaryKybRelatedDocuments | null>(null);

  const fetchDocuments = useCallback(async () => {
    setSubmitting(true);

    const response = await apiRequest(`/business_claim/summary_kyb_related_documents?entity_id=${organizationId}`, 'GET'); 
    
    if (response.success) {
      setDocuments(response.data);
      setSubmitting(false);
    } else {
      console.error('Error fetching documents:', response.error);
      setSubmitting(false);
      setDocuments(null);
    }
  }, [organizationId]);

  useEffect(() => {
    if (documents === null) {
      fetchDocuments();
    }
  }, [documents, fetchDocuments]);
  
  return (
    <div className="col">
      <div className="d-flex flex-wrap">
        <div className="col-10 w-100">
          {submitting ? (
            <div className="d-flex justify-content-center align-items-center">
              <span className="size-14 font-weight-600">{t('kyb_flow.company_documents.loading')}</span>
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {documents?.pacto_social?.length && documents?.pacto_social ? (
                <DocumentItem
                  title={t('kyb_flow.company_documents.social_pact')}
                  description={t('kyb_flow.company_documents.social_pact_description')}
                  hasMore={false}
                />
              ) : null}
              {documents?.acta?.length && documents?.acta ? (
                <DocumentItem
                  title={t('kyb_flow.company_documents.minutes')}
                  description={t('kyb_flow.company_documents.minutes_description')}
                  hasMore={false}
                />
              ) : null}
              <DocumentItem
                title={t('kyb_flow.company_documents.public_registry_report')}
                description={t('kyb_flow.company_documents.public_registry_report_description')}
                hasMore={false}
              />
              {documents?.avisos?.length && documents?.avisos ? (
                <DocumentItem
                  title={t('kyb_flow.company_documents.operation_permit')}
                  description={t('kyb_flow.company_documents.operation_permit_description')}
                  hasMore={false}
                  lastItem={true}
                />
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface DocumentItemProps {
  title: string;
  description: string;
  hasMore?: boolean;
  onClick?: () => void;
  lastItem?: boolean;
  children?: React.ReactNode;
}

interface CompanyDocumentsProps {
  organizationId: string;
}
